import { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Autocomplete from '@mui/material/Autocomplete';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useAssignStore } from '@libs/store/talents';
import {  Flex, PrimaryDialog, PrimaryDialogProps } from '@atoms/layout';
import { LoadingButton, Button } from '@atoms/buttons';
import { TextField } from '@atoms/inputs';
import { FaIcon } from '@atoms/icons';
import { prepareTalentsOptions } from '../helpers';
import { Box, Stack, Tooltip } from '@mui/material';
import { OrganizationAvatar } from '@atoms/dataDisplay';
import { useLocationStore } from '@libs/store/locations';

type AssignToMultipleShiftsDialogProps = Omit<PrimaryDialogProps, 'onSubmit'> & {
  shiftsNumber: number;
  onSubmit?: (talent: string) => void;
  loading?: boolean;
  locationId?: string;
  qualifications?: string[];
  allowOnlyInternals?: boolean;
};
export function AssignToMultipleShiftsDialog({
  shiftsNumber,
  onSubmit,
  loading,
  locationId,
  qualifications,
  allowOnlyInternals,
  ...props
}: AssignToMultipleShiftsDialogProps) {
  return (
    <PrimaryDialog fullWidth {...props}>
      <Content
        shiftsNumber={shiftsNumber}
        onCloseClick={() => props.onClose?.({}, 'escapeKeyDown')}
        onSubmit={onSubmit}
        loading={loading}
        locationId={locationId}
        qualifications={qualifications}
        allowOnlyInternals={allowOnlyInternals}
      />
    </PrimaryDialog>
  );
}

type ContentProps = {
  shiftsNumber: number;
  onCloseClick: () => void;
  onSubmit?: (talent: string) => void;
  loading?: boolean;
  locationId?: string;
  qualifications?: string[];
  allowOnlyInternals?: boolean;
};
function Content({ shiftsNumber, onCloseClick, onSubmit, loading = false, locationId, qualifications, allowOnlyInternals }: ContentProps) {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const [assigneeValue, setAssigneeValue] = useState<string>('');
  const debouncedValue = useDebounce(assigneeValue, 300);
  const { currentLocationID } = useLocationStore();
  const [selected, setSelected] = useState<{
    label: string;
    richLabel: JSX.Element;
    value: string;
  } | null>();
  const loadTalents = useAssignStore((s) => s.loadData);
  const loadingTalents = useAssignStore((s) => s.loading);
  const talents = useAssignStore((s) => s.data);
  const options = useMemo(() => {
    return prepareTalentsOptions(talents, undefined, [], currentLocationID).filter((talent)=> allowOnlyInternals ? !!talent.agency == false : true).map((talent) => ({
      ...talent,
      label: talent.label,
      richLabel: (
        <>
          <Stack direction="row" alignItems="center" width="100%"  spacing={1}>
            <Box width={'20px'}>
              {talent.agency && (
                <Tooltip title={talent.agency.name} placement="right">
                  <OrganizationAvatar
                    src={talent.agency.logoUrl}
                    fullName={talent.agency.name}
                    sx={{ width: '20px', height: '20px' }}
                  />
                </Tooltip>
              )}
            </Box>
            <Box width={'16px'}>
              {talent.isOriented && (
                <FaIcon
                  name="square-check"
                  color="#4caf50"
                  variant="solid"
                  style={{
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    width: '16px',
                    height: '16px',
                  }}
                />
              )}
            </Box>
            <Typography>{talent.label}</Typography>
            <Box flex={1}/>
            <Typography variant="caption" color="text.secondary">
              {(talent.qualifications || []).map((q) => q.abbreviation).join(', ')}
            </Typography>
          </Stack>
        </>
      ),
      value: talent.id,
    }));
  }, [talents]);
  useEffect(() => {
    if (locationId) {
      loadTalents(debouncedValue, {
        locations: { key: 'locations', value: [locationId] },
        qualifications: { key: 'qualifications', value: qualifications ?? [] },
      });
    }
  }, [loadTalents, locationId, debouncedValue, qualifications]);

  const handleAssigneeChange: FormEventHandler<HTMLDivElement> = (evt) => {
    const target = evt.target as HTMLInputElement;
    setAssigneeValue(target.value);
  };

  return (
    <>
      <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
        <Flex justifyContent="space-between">
          <Flex column gap={1}>
            <Typography variant="h5">
              <FormattedMessage id="shifts.recurrent.assignDialog.title" />
            </Typography>
          </Flex>
          <Box>
            <IconButton sx={{ width: '40px', position: 'relative', top: -5, right: -5 }} onClick={onCloseClick}>
              <FaIcon name="xmark" variant="light" />
            </IconButton>
          </Box>
        </Flex>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" noWrap>
          <FormattedMessage id="shifts.recurrent.assignDialog.shiftNumber" values={{ number: shiftsNumber }} />
        </Typography>

        <Flex mt={2}>
          <Autocomplete
            fullWidth
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            groupBy={(option) => {
              const talent = options.find((t) => t.value === option.value);
              return intl.formatMessage({ id: `talents.selector.stuffingType.${talent?.groupBy ?? 'Internal'}` });
            }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={<FormattedMessage id="shifts.recurrent.assignDialog.selectLabel" />}
                placeholder={intl.formatMessage({ id: 'shifts.recurrent.assignDialog.selectLabel' })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingTalents ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={loadingTalents}
            onInput={handleAssigneeChange}
            onChange={(_, value) => {
              setSelected(value);
            }}
            value={selected}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.richLabel}
              </li>
            )}
          />
        </Flex>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box color="text.secondary">
          <Button variant="text" color="inherit" rounded onClick={onCloseClick} size="large">
            <FormattedMessage id="common.cancel" />
          </Button>
        </Box>
        <LoadingButton
          loading={loading}
          variant="contained"
          rounded
          type="submit"
          size="large"
          onClick={() => selected && onSubmit?.(selected.value)}
        >
          <FormattedMessage id="common.submit" />
        </LoadingButton>
      </DialogActions>
    </>
  );
}
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
